import type { GigsQueryPayload } from "api/queries/stafferGigs";
import type { GigStatus, GigType } from "api/resources/shared";
import type { GigView } from "domains/gigs/containers/ViewSwitcher/gigView";
import * as R from "remeda";
import {
  adminFilterKeys,
  type GigStatusFilterOptions,
  gigStatusOptionIds,
  gigTypeDefaultIds,
} from "../containers/Filters/constants";
import type { MarketplaceSearch } from "./useMarketplaceSearch";

type UiType = NonNullable<MarketplaceSearch["types"]>[number];

/** Without tasks a `task` in the URL is ignored; a filter that was only `task` (picked in the calendar) reads as unfiltered, never as none. */
export function visibleTypes(types: ReadonlyArray<UiType>, includeTasks: boolean | undefined): Array<UiType> {
  const shown = types.filter((type) => includeTasks || type !== "task");
  return types.length > 0 && shown.length === 0 ? gigTypeDefaultIds(includeTasks) : shown;
}

/** `includeTasks`: `qwick-tasks` is on, so the Task Gig Type is offered and, unfiltered, requested. Off, a `task` in the URL is ignored. */
export function getScheduleParams(search: MarketplaceSearch, view: GigView, { includeTasks = false } = {}) {
  const { ordering, positions, statuses: rawStatuses, types: rawTypes, events, ...searchParams } = search;
  const statuses = rawStatuses ?? gigStatusOptionIds.filter((status) => status !== "cancelled");
  const types: Array<UiType> | undefined = rawTypes
    ? visibleTypes(rawTypes, includeTasks)
    : gigTypeDefaultIds(includeTasks);

  const adminFilters: Partial<typeof searchParams> = view === "list" ? R.pick(searchParams, adminFilterKeys) : {};

  const params: GigsQueryPayload = {
    ordering,
    skill_ids: positions?.length ? positions : undefined,
    statuses: statuses?.flatMap(mapUiStatusToApiStatus),
    types: adminFilters.type ? [adminFilters.type] : types?.flatMap(mapUiTypeToApiType),
    has_event: events?.length === 2 ? undefined : events?.includes("events"),
    employment_types: adminFilters.employment_type ? [adminFilters.employment_type] : undefined,
    ...adminFilters,
    skill_id: adminFilters.skill_id != null ? [adminFilters.skill_id] : undefined,
  };

  const enabled = statuses?.length !== 0 && types?.length !== 0 && events?.length !== 0;

  return { params, enabled };
}

function mapUiStatusToApiStatus(status: GigStatusFilterOptions["id"]): Array<GigStatus> {
  switch (status) {
    case "unfilled":
      return ["UNFILLED", "UNAPPROVED", "POSTED"];
    case "ready":
      return ["CONFIRMED"];
    case "paymentDue":
      return ["INCOMPLETE"];
    case "paymentError":
      return ["FAILED_PAYMENT"];
    case "cancelled":
      return ["CANCELLED"];
    case "completed":
      return ["COMPLETED"];
  }
}

function mapUiTypeToApiType(type: UiType): Array<GigType | "TASK"> {
  switch (type) {
    case "marketplace":
      return ["MARKETPLACE", "INTERVIEW", "PRIVATE_DIRECT_OFFER"];
    case "network":
      return ["NETWORK"];
    case "task":
      return ["TASK"];
  }
}

/** `jobs-in-main-menu` on (with Jobs access): working-interview shifts come back alongside the other types, unless a Gig Type filter has narrowed them out. */
export const withWorkingInterviews = (types: GigsQueryPayload["types"], include: boolean | undefined) => {
  if (!include) return types;
  const unfiltered = !types || (types.includes("MARKETPLACE") && types.includes("NETWORK"));
  return unfiltered || types.includes("WORKING_INTERVIEW")
    ? [...new Set([...(types ?? []), "WORKING_INTERVIEW" as const])]
    : types;
};
